// @ds/editable/fieldTypeRegistry — single source of truth for "how is
// each EditableFieldType variant rendered?"
//
// Both `<SchemaFormItem>` (form context) and `<Editable>` (inline-edit
// context) call into this one function. New field types add ONE entry,
// both contexts inherit. The previous two-parallel-switch architecture
// drifted in practice — weight had different `step`, date used raw
// DatePicker vs DatePickerField, boolean used Checkbox vs Switch — and
// new variants (radio-group, country-select, category-select) ended up
// in only one dispatcher.
//
// The widget contract: each render returns a single React element that
// honors AntD's Form.Item child contract — accepts `value` + `onChange`
// props which Form.Item injects via cloneElement in form context, and
// which `<Editable>` wires manually in inline context. The render does
// NOT decide the commit lifecycle (commit-on-blur vs commit-on-change);
// that's the consumer's responsibility (Editable wraps onChange in its
// state machine; Form.Item routes onChange to its values store).
//
// Drift fixed by this consolidation:
//   - weight: was `step={0.001}` inline / unset in form → unified to 0.001
//   - boolean: was Checkbox inline / Switch in form → unified to Switch
//   - date: was raw `<DatePicker>` inline / `<DatePickerField>` in form →
//     unified to `<DatePickerField>` (handles Dayjs↔string conversion)
//   - radio-group, country-select, category-select: were form-only →
//     now work in inline-edit too

import React from "react";
import type { ReactNode } from "react";
import { Input, InputNumber, Switch } from "antd";

import type { EditableFieldType } from "@ds/editable/Editable";
import { Select, type SelectOption } from "@ds/forms/Select";
import { DatePickerField } from "@ds/forms/DatePickerField";
import { TimePickerField } from "@ds/forms/TimePickerField";
import { EntitySelect } from "@ds/connected/EntitySelect";
import { RadioGroupField } from "@ds/editable/RadioGroupField";
import { JsonTextArea } from "@ds/editable/JsonTextArea";
import {
  CategorySelect,
  CountrySelect,
} from "@ds/connected/StaticSelects";

// ─── Context passed by each consumer ─────────────────────────────────────────
//
// Form context: passes the inputs Form.Item will inject (value/onChange are
// undefined; cloneElement replaces them after render).
// Inline-edit context: passes value/onChange/onKeyDown/onBlur from the
// editor lifecycle; also size:"small" and status:"error" when invalid.

export interface FieldWidgetCtx {
  /** Current value. Form context leaves undefined (Form.Item injects). */
  value?: unknown;
  /** Change handler. Form context leaves undefined (Form.Item injects). */
  onChange?: (v: unknown) => void;
  disabled?: boolean;
  /** Resolved placeholder string (i18n already applied by the caller). */
  placeholder?: string;
  autoFocus?: boolean;
  /** Inline-edit passes "small"; form context omits. */
  size?: "small";
  /** Inline-edit passes "error" when validation fails; form context omits. */
  status?: "error";
  testId?: string;
  /** Inline-edit's Enter/Escape lifecycle. Form context omits. */
  onKeyDown?: (e: React.KeyboardEvent) => void;
  /** Inline-edit's commit-on-blur for text-like inputs. Form context omits. */
  onBlur?: (e: React.FocusEvent) => void;
  /** Inline-edit may open dropdowns by default (skip the click-to-open step). */
  defaultOpen?: boolean;
  /**
   * Per-call extraOption — only honored by `entity-select`. Pre-resolved
   * `{value, label}` for the currently-stored id (typically from the
   * form's hydrate query joining the relation). Lets the Select render
   * the proper label without waiting for the list query to land.
   *
   * Schema-level alternative: `edit.field.displayLabel: (record) => string`
   * gets resolved by the dispatcher when a record context is available
   * (inline edit). For form context with a hydrated record, the
   * orchestrator computes the option from the record and passes here.
   */
  extraOption?: { value: number | string; label: ReactNode } | null;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

/** Pick value props that should be passed only when the consumer wired them
 *  (inline-edit). Form context leaves both undefined so Form.Item's
 *  cloneElement injection wins. */
function valueProps<V>(ctx: FieldWidgetCtx, cast: (v: unknown) => V) {
  if (ctx.onChange === undefined) return {};
  return {
    value: cast(ctx.value),
    onChange: ctx.onChange,
  };
}

/**
 * Translate option labels for variants that carry them. Schemas store i18n
 * keys in option labels (`contacts.purposes.sales`, `price_lists.side.load`)
 * so the locale file remains the single source of truth — `t(key, key)`
 * returns the translated value or the raw key string when missing.
 *
 * Used by BOTH SchemaFormItem (form context) AND Editable (inline-edit
 * context) before they hand the field to `renderFieldWidget`. Keeping the
 * translation step outside the registry lets the registry stay pure
 * (no React hooks / i18n dependency); both consumers already have `t`.
 *
 * Covers: select, multiselect, radio-group. Defensively skips when
 * `options` is absent (e.g. companySchema.categories declares the
 * non-standard `kind: "category"` variant without options — the widget
 * override path handles it).
 */
export function resolveOptionLabels(
  field: EditableFieldType,
  resolve: (key: string) => string,
): EditableFieldType {
  if (
    field.type !== "select" &&
    field.type !== "multiselect" &&
    field.type !== "radio-group"
  ) {
    return field;
  }
   
  const options = (field as { options?: unknown }).options as
    | Array<{ value: unknown; label: unknown }>
    | undefined;
  if (!Array.isArray(options)) return field;
  const translated = options.map((o) => ({
    value: o.value,
    label: typeof o.label === "string" ? resolve(o.label) : o.label,
  }));
  return { ...field, options: translated } as EditableFieldType;
}

/** CategorySelect is typed against single-pick CompanyCategory; multi mode is
 *  AntD-Select-spread. Its own prop types are the cast target at that boundary. */
type CategorySelectProps = React.ComponentProps<typeof CategorySelect>;

// ─── The dispatcher ─────────────────────────────────────────────────────────

export function renderFieldWidget(
  field: EditableFieldType,
  ctx: FieldWidgetCtx,
): ReactNode {
  const tid = ctx.testId ? { "data-testid": ctx.testId } : {};
  const sizeProp = ctx.size ? { size: ctx.size } : {};
  const statusProp = ctx.status ? { status: ctx.status } : {};
  // Captured so the conditionally-spread handlers below keep the narrowing
  // that `ctx.onChange && …` establishes.
  const onChange = ctx.onChange;

  switch (field.type) {
    case "text":
      return (
        <Input
          {...valueProps(ctx, (v) => (v as string | null) ?? "")}
          disabled={ctx.disabled}
          placeholder={ctx.placeholder}
          autoFocus={ctx.autoFocus}
          maxLength={field.maxLength}
          onKeyDown={ctx.onKeyDown}
          onBlur={ctx.onBlur}
          {...sizeProp}
          {...statusProp}
          {...tid}
          {...(onChange
            ? {
                onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
                  onChange(e.target.value),
              }
            : {})}
        />
      );

    case "textarea":
      return (
        <Input.TextArea
          {...(onChange !== undefined
            ? { value: (ctx.value as string | null) ?? "" }
            : {})}
          disabled={ctx.disabled}
          placeholder={ctx.placeholder}
          autoFocus={ctx.autoFocus}
          rows={field.rows ?? 3}
          maxLength={field.maxLength}
          onKeyDown={ctx.onKeyDown}
          onBlur={ctx.onBlur}
          {...sizeProp}
          {...statusProp}
          {...tid}
          {...(onChange
            ? {
                onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) =>
                  onChange(e.target.value),
              }
            : {})}
        />
      );

    case "json":
      // JsonTextArea owns the object→text mapping, so it applies in BOTH
      // contexts — including form context, where Form.Item cloneElement
      // injects the hydrated jsonb object as `value` after this render.
      return (
        <JsonTextArea
          {...(onChange !== undefined
            ? { value: ctx.value, onChange: (v: string) => onChange(v) }
            : {})}
          disabled={ctx.disabled}
          placeholder={ctx.placeholder}
          autoFocus={ctx.autoFocus}
          rows={field.rows ?? 4}
          onKeyDown={ctx.onKeyDown}
          onBlur={ctx.onBlur as unknown as (() => void) | undefined}
          {...(ctx.status ? { status: ctx.status } : {})}
          testId={ctx.testId}
        />
      );

    case "number":
      return (
        <InputNumber
          style={{ width: "100%" }}
          {...valueProps(ctx, (v) => v as number | null)}
          disabled={ctx.disabled}
          placeholder={ctx.placeholder}
          autoFocus={ctx.autoFocus}
          precision={field.precision}
          min={field.min}
          max={field.max}
          step={field.step}
          addonAfter={field.unit}
          decimalSeparator=","
          onKeyDown={ctx.onKeyDown}
          onBlur={ctx.onBlur}
          {...sizeProp}
          {...statusProp}
          {...tid}
        />
      );

    case "weight":
      return (
        <InputNumber
          style={{ width: "100%" }}
          {...valueProps(ctx, (v) => v as number | null)}
          disabled={ctx.disabled}
          placeholder={ctx.placeholder ?? "0,000"}
          autoFocus={ctx.autoFocus}
          precision={3}
          min={0}
          step={0.001}
          addonAfter="t"
          decimalSeparator=","
          onKeyDown={ctx.onKeyDown}
          onBlur={ctx.onBlur}
          {...sizeProp}
          {...statusProp}
          {...tid}
        />
      );

    case "currency":
      return (
        <InputNumber
          style={{ width: "100%" }}
          {...valueProps(ctx, (v) => v as number | null)}
          disabled={ctx.disabled}
          placeholder={ctx.placeholder}
          autoFocus={ctx.autoFocus}
          precision={field.precision ?? 2}
          min={0}
          step={0.01}
          addonAfter={field.currency ?? "zł"}
          decimalSeparator=","
          onKeyDown={ctx.onKeyDown}
          onBlur={ctx.onBlur}
          {...sizeProp}
          {...statusProp}
          {...tid}
        />
      );

    case "date":
      // DatePickerField handles Dayjs↔string ("YYYY-MM-DD") conversion.
      return (
        <DatePickerField
          {...(onChange !== undefined
            ? { value: ctx.value as string | undefined }
            : {})}
          disabled={ctx.disabled}
          placeholder={ctx.placeholder}
          autoFocus={ctx.autoFocus}
          style={{ width: "100%" }}
          testId={ctx.testId}
          onKeyDown={ctx.onKeyDown}
          {...sizeProp}
          {...statusProp}
          {...(onChange
            ? { onChange: (v: string | null) => onChange(v) }
            : {})}
        />
      );

    case "time":
      return (
        <TimePickerField
          {...(onChange !== undefined
            ? { value: ctx.value as string | undefined }
            : {})}
          disabled={ctx.disabled}
          placeholder={ctx.placeholder}
          autoFocus={ctx.autoFocus}
          testId={ctx.testId}
          {...sizeProp}
          {...statusProp}
          {...(onChange
            ? { onChange: (v: string | null) => onChange(v) }
            : {})}
        />
      );

    case "boolean":
      // Switch (not Checkbox) — visible toggle works in both inline and
      // form contexts. Form.Item passes via `valuePropName="checked"`.
      // Note: Switch doesn't accept onKeyDown — toggling is click-only.
      // Inline-edit's Enter-to-commit lifecycle is moot here (the switch
      // commits-on-change anyway).
      return (
        <Switch
          {...(onChange !== undefined
            ? { checked: !!(ctx.value as boolean | undefined) }
            : {})}
          disabled={ctx.disabled}
          autoFocus={ctx.autoFocus}
          {...sizeProp}
          {...tid}
          {...(onChange
            ? { onChange: (checked: boolean) => onChange(checked) }
            : {})}
        />
      );

    case "select":
      // DS Select (dropdown variant) — themed AntD Select behind the DS API.
      // `ctx.size` is AntD "small"; map to the DS "sm" scale. `defaultOpen`
      // opens-on-mount but stays closable (inline-edit). `id` is forwarded so
      // Form.Item label association / scroll-to-error keep working.
      return (
        <Select<string | number>
          variant="dropdown"
          {...valueProps(ctx, (v) => v as string | number | undefined)}
          options={(field.options ?? []) as SelectOption<string | number>[]}
          disabled={ctx.disabled}
          placeholder={ctx.placeholder}
          autoFocus={ctx.autoFocus}
          allowClear={field.allowClear}
          size={ctx.size ? "sm" : undefined}
          onKeyDown={ctx.onKeyDown}
          onBlur={ctx.onBlur as unknown as (() => void) | undefined}
          {...(ctx.status ? { status: ctx.status } : {})}
          {...(ctx.defaultOpen ? { defaultOpen: true } : {})}
          testId={ctx.testId}
        />
      );

    case "multiselect":
      return (
        <Select<string | number>
          variant="dropdown"
          multiple
          {...(onChange !== undefined
            ? {
                value: (ctx.value as Array<string | number> | null) ?? [],
                onChange: (v) => onChange(v),
              }
            : {})}
          options={(field.options ?? []) as SelectOption<string | number>[]}
          disabled={ctx.disabled}
          placeholder={ctx.placeholder}
          autoFocus={ctx.autoFocus}
          maxTagCount="responsive"
          size={ctx.size ? "sm" : undefined}
          onKeyDown={ctx.onKeyDown}
          onBlur={ctx.onBlur as unknown as (() => void) | undefined}
          {...(ctx.status ? { status: ctx.status } : {})}
          {...(ctx.defaultOpen ? { defaultOpen: true } : {})}
          testId={ctx.testId}
        />
      );

    case "radio-group":
      return (
        <RadioGroupField
          testId={ctx.testId}
          disabled={ctx.disabled}
          size={ctx.size}
          options={field.options}
          bind={valueProps(ctx, (v) => v)}
        />
      );

    case "entity-select":
      // Multi-mode suppresses allowClear (tag chips render their own ×).
      return (
        <EntitySelect
          kind={field.kind}
          {...valueProps(ctx, (v) => (v as number | null) ?? undefined)}
          disabled={ctx.disabled}
          placeholder={ctx.placeholder}
          autoFocus={ctx.autoFocus}
          {...(field.mode === "multiple"
            ? { mode: "multiple" as const }
            : { allowClear: true })}
          {...(field.role ? { role: field.role } : {})}
          {...(field.category ? { category: field.category } : {})}
          {...(field.requireNip ? { requireNip: true } : {})}
          {...(field.onlyEdprEligible ? { onlyEdprEligible: true } : {})}
          {...(ctx.extraOption != null
            ? { extraOption: ctx.extraOption }
            : field.displayLabel != null
              ? { extraOption: { value: 0, label: field.displayLabel } }
              : {})}
          style={{ width: "100%" }}
          {...sizeProp}
          {...statusProp}
          {...(ctx.defaultOpen ? { defaultOpen: true } : {})}
          testId={ctx.testId}
          onKeyDown={ctx.onKeyDown}
          onBlur={ctx.onBlur as React.FocusEventHandler<HTMLElement> | undefined}
        />
      );

    case "country-select":
      return (
        <CountrySelect
          {...valueProps(ctx, (v) => v as string | undefined)}
          disabled={ctx.disabled}
          placeholder={ctx.placeholder}
          autoFocus={ctx.autoFocus}
          allowClear={field.allowClear ?? true}
          style={{ width: "100%" }}
          {...sizeProp}
          {...statusProp}
          {...(ctx.defaultOpen ? { defaultOpen: true } : {})}
          {...tid}
        />
      );

    case "category-select":
      return (
        <CategorySelect
          // CategorySelect is typed against single-pick CompanyCategory;
          // multi mode is AntD-Select-spread. Cast at the boundary.
          mode={(field.mode ?? "multiple") === "multiple" ? "multiple" : (undefined as CategorySelectProps["mode"])}
          {...(onChange !== undefined
            ? {
                // Normalize to an array so a `null`/non-array value doesn't make
                // the multiple-mode Select render empty (mirrors `multiselect`).
                value: ((ctx.value as Array<string | number> | null) ??
                  []) as unknown as CategorySelectProps["value"],
                onChange: onChange as CategorySelectProps["onChange"],
              }
            : {})}
          disabled={ctx.disabled}
          placeholder={ctx.placeholder}
          autoFocus={ctx.autoFocus}
          maxTagCount="responsive"
          allowClear
          // AntD-based connected select doesn't fill its container by default
          // (unlike @ds/forms/Select); stretch it so inline-edit isn't cramped.
          style={{ width: "100%" }}
          {...sizeProp}
          {...statusProp}
          {...tid}
        />
      );

    default: {
      const _exhaustive: never = field;
      void _exhaustive;
      return (
        <Input
          disabled={ctx.disabled}
          placeholder={ctx.placeholder}
          {...tid}
        />
      );
    }
  }
}
